import React, { useState, useEffect, useCallback } from 'react';
import PayoffDiagram from './components/PayoffDiagram';
import SecurityPanel from './components/SecurityPanel';
import './App.css';

const newId = () => crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function compressState(data) {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(data))));
  } catch {
    return encodeURIComponent(JSON.stringify(data));
  }
}

function decompressState(str) {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(str))));
  } catch {
    try {
      return JSON.parse(decodeURIComponent(str));
    } catch {
      return null;
    }
  }
}

function parseStateFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get('s');
  if (!encoded) return null;
  try {
    const data = decompressState(encoded);
    if (!data || !Array.isArray(data.securities)) return null;
    return {
      securities: data.securities.map(s => ({
        ...s,
        id: newId(),
        quantity: Number(s.quantity) || 1,
        strikePrice: Number(s.strikePrice) ?? 100,
        faceValue: Number(s.faceValue) ?? 1000,
        purchasePrice: Number(s.purchasePrice) ?? 0,
        premium: Number(s.premium) || 0,
        position: s.position || 'long'
      })),
      axisMin: s(data.axisMin),
      axisMax: s(data.axisMax)
    };
  } catch {
    return null;
  }
}

function s(v) {
  return v != null && v !== '' ? String(v) : '';
}

function buildShareUrl(securities, axisMin, axisMax) {
  const data = {
    securities: securities.map(({ id, ...rest }) => rest),
    axisMin: axisMin || '',
    axisMax: axisMax || ''
  };
  const encoded = compressState(data);
  const url = new URL(window.location.href);
  url.searchParams.set('s', encoded);
  return url.toString();
}

function pushStateToUrl(securities, axisMin, axisMax) {
  window.history.replaceState({}, '', buildShareUrl(securities, axisMin, axisMax));
}

const PRESETS = {
  'Long Straddle': [
    { type: 'Call', quantity: 1, strikePrice: 100, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Put', quantity: 1, strikePrice: 100, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 }
  ],
  'Strangle': [
    { type: 'Call', quantity: 1, strikePrice: 110, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Put', quantity: 1, strikePrice: 90, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 }
  ],
  'Bull Call Spread': [
    { type: 'Call', quantity: 1, strikePrice: 90, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Call', quantity: 1, strikePrice: 110, faceValue: 1000, purchasePrice: 0, position: 'short', premium: 0 }
  ],
  'Bear Put Spread': [
    { type: 'Put', quantity: 1, strikePrice: 110, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Put', quantity: 1, strikePrice: 90, faceValue: 1000, purchasePrice: 0, position: 'short', premium: 0 }
  ],
  'Covered Call': [
    { type: 'Stock', quantity: 1, strikePrice: 100, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Call', quantity: 1, strikePrice: 110, faceValue: 1000, purchasePrice: 0, position: 'short', premium: 0 }
  ],
  'Protective Put': [
    { type: 'Stock', quantity: 1, strikePrice: 100, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Put', quantity: 1, strikePrice: 95, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 }
  ],
  'Collar': [
    { type: 'Stock', quantity: 1, strikePrice: 100, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Put', quantity: 1, strikePrice: 90, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Call', quantity: 1, strikePrice: 110, faceValue: 1000, purchasePrice: 0, position: 'short', premium: 0 }
  ],
  'Long Butterfly': [
    { type: 'Call', quantity: 1, strikePrice: 90, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Call', quantity: 2, strikePrice: 100, faceValue: 1000, purchasePrice: 0, position: 'short', premium: 0 },
    { type: 'Call', quantity: 1, strikePrice: 110, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 }
  ],
  'Iron Condor': [
    { type: 'Put', quantity: 1, strikePrice: 80, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 },
    { type: 'Put', quantity: 1, strikePrice: 90, faceValue: 1000, purchasePrice: 0, position: 'short', premium: 0 },
    { type: 'Call', quantity: 1, strikePrice: 110, faceValue: 1000, purchasePrice: 0, position: 'short', premium: 0 },
    { type: 'Call', quantity: 1, strikePrice: 120, faceValue: 1000, purchasePrice: 0, position: 'long', premium: 0 }
  ]
};

const initialUrlState = parseStateFromUrl();
const initialSecurities = initialUrlState ? initialUrlState.securities : [];

function App() {
  const [history, setHistory] = useState([initialSecurities]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const securities = history[historyIndex];

  const [showIndividual, setShowIndividual] = useState(true);
  const [axisMin, setAxisMin] = useState(initialUrlState ? initialUrlState.axisMin : '');
  const [axisMax, setAxisMax] = useState(initialUrlState ? initialUrlState.axisMax : '');
  const [showNetPnL, setShowNetPnL] = useState(true);
  const [showPayoffTable, setShowPayoffTable] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  const pushSecurities = useCallback((newSecurities) => {
    const trimmed = history.slice(0, historyIndex + 1);
    trimmed.push(newSecurities);
    setHistory(trimmed);
    setHistoryIndex(trimmed.length - 1);
  }, [history, historyIndex]);

  const undo = useCallback(() => {
    setHistoryIndex(i => Math.max(0, i - 1));
  }, []);

  const redo = useCallback(() => {
    setHistoryIndex(i => Math.min(history.length - 1, i + 1));
  }, [history.length]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    pushStateToUrl(securities, axisMin, axisMax);
  }, [securities, axisMin, axisMax]);

  const handleCopyShareLink = async () => {
    const url = buildShareUrl(securities, axisMin, axisMax);
    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback('Copied!');
    } catch {
      setCopyFeedback('Copy failed');
    }
    setTimeout(() => setCopyFeedback(null), 2000);
  };

  const loadPreset = (presetKey) => {
    const template = PRESETS[presetKey];
    if (!template) return;
    pushSecurities(template.map(s => ({ ...s, id: newId() })));
    setAxisMin('');
    setAxisMax('');
  };

  const addSecurity = (type = 'Call') => {
    pushSecurities([...securities, {
      id: newId(),
      type,
      quantity: 1,
      strikePrice: 100,
      faceValue: 1000,
      purchasePrice: 0,
      position: 'long',
      premium: 0
    }]);
  };

  const handleAddPosition = (e) => {
    const type = e.target.value;
    if (type) {
      addSecurity(type);
      e.target.value = '';
    }
  };

  const updateSecurity = (id, updatedSecurity) => {
    pushSecurities(securities.map(sec => sec.id === id ? updatedSecurity : sec));
  };

  const removeSecurity = (id) => {
    pushSecurities(securities.filter(sec => sec.id !== id));
  };

  const moveSecurity = (id, direction) => {
    const idx = securities.findIndex(sec => sec.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= securities.length) return;
    const updated = [...securities];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    pushSecurities(updated);
  };

  const clearAll = () => {
    pushSecurities([]);
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>Options Payoff Illustrator</h1>
        <button
          type="button"
          className="theme-toggle"
          onClick={() => setDarkMode(!darkMode)}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          title={darkMode ? 'Light mode' : 'Dark mode'}
        >
          {darkMode ? '\u2600' : '\u263E'}
        </button>
      </header>

      <div className="controls-bar">
        <div className="control-group">
          <span className="control-group-label">Positions</span>
          <select
            className="control-select"
            value=""
            onChange={handleAddPosition}
            aria-label="Add position"
          >
            <option value="" disabled>Add position...</option>
            <option value="Stock">Stock</option>
            <option value="Bond">Bond</option>
            <option value="Call">Call Option</option>
            <option value="Put">Put Option</option>
          </select>
          <select
            className="control-select"
            value=""
            onChange={(e) => { const v = e.target.value; if (v) loadPreset(v); e.target.value = ''; }}
            aria-label="Load preset strategy"
          >
            <option value="" disabled>Load strategy...</option>
            {Object.keys(PRESETS).map(key => (
              <option key={key} value={key}>{key}</option>
            ))}
          </select>
          {securities.length > 0 && (
            <button type="button" className="btn btn-danger-outline btn-sm" onClick={clearAll}>
              Clear All
            </button>
          )}
        </div>

        <div className="control-group">
          <span className="control-group-label">Display</span>
          <label className="checkbox-label">
            <input type="checkbox" checked={showIndividual} onChange={(e) => setShowIndividual(e.target.checked)} />
            <span>Individual</span>
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={showNetPnL} onChange={(e) => setShowNetPnL(e.target.checked)} />
            <span>Net P&amp;L</span>
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={showPayoffTable} onChange={(e) => setShowPayoffTable(e.target.checked)} />
            <span>Table</span>
          </label>
        </div>

        <div className="control-group">
          <span className="control-group-label">Price Range</span>
          <input
            type="number"
            className="axis-input"
            placeholder="Min"
            value={axisMin}
            onChange={(e) => setAxisMin(e.target.value)}
            aria-label="Price axis minimum"
          />
          <span className="axis-separator">&ndash;</span>
          <input
            type="number"
            className="axis-input"
            placeholder="Max"
            value={axisMax}
            onChange={(e) => setAxisMax(e.target.value)}
            aria-label="Price axis maximum"
          />
        </div>

        <div className="control-group">
          <button type="button" className="btn btn-sm" onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)">
            Undo
          </button>
          <button type="button" className="btn btn-sm" onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Y)">
            Redo
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopyShareLink}>
            Share
          </button>
          {copyFeedback && <span className="copy-feedback" aria-live="polite">{copyFeedback}</span>}
        </div>
      </div>

      <div className="main-content">
        <div className="securities-panel">
          <div className="securities-container">
            {securities.length === 0 ? (
              <div className="empty-state" aria-live="polite">
                <p>Build a strategy by adding positions, or load a preset.</p>
                <div className="empty-state-actions">
                  <button type="button" className="btn btn-primary" onClick={() => addSecurity('Call')}>
                    Add Call Option
                  </button>
                  <button type="button" className="btn" onClick={() => addSecurity('Stock')}>
                    Add Stock
                  </button>
                </div>
              </div>
            ) : (
              securities.map((security, index) => (
                <SecurityPanel
                  key={security.id}
                  security={security}
                  onUpdate={(updated) => updateSecurity(security.id, updated)}
                  onRemove={() => removeSecurity(security.id)}
                  onMoveUp={index > 0 ? () => moveSecurity(security.id, -1) : null}
                  onMoveDown={index < securities.length - 1 ? () => moveSecurity(security.id, 1) : null}
                />
              ))
            )}
          </div>
        </div>
        <PayoffDiagram
          securities={securities}
          showIndividual={showIndividual}
          showNetPnL={showNetPnL}
          showPayoffTable={showPayoffTable}
          xMin={axisMin}
          xMax={axisMax}
          darkMode={darkMode}
        />
      </div>
    </div>
  );
}

export default App;
